import { Refine, AuthProvider } from "@pankod/refine-core";
import {
  notificationProvider,
  RefineSnackbarProvider,
  CssBaseline,
  GlobalStyles,
  ReadyPage,
  ErrorComponent,
} from "@pankod/refine-mui";
import { PeopleAltOutlined, WalletOutlined } from "@mui/icons-material";
import TimelineIcon from "@mui/icons-material/Timeline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dataProvider from "@pankod/refine-simple-rest";
import routerProvider from "@pankod/refine-react-router-v6";
import axios, { AxiosRequestConfig } from "axios";
import { Title, Sider, Layout, Header } from "components/layout";
import { ColorModeContextProvider } from "contexts";
import { CredentialResponse } from "interfaces/google";
import { Login, Home } from "pages";
import AllStudents from "pages/Students/all-students";
import StudentDetails from "pages/Students/student-details";
import CreateStudent from "pages/Students/create-student";
import EditStudent from "pages/Students/edit-student";
import LessonSchedule from "pages/LessonSchedule/lesson-schedule";
import AllEarnings from "pages/Earnings/all-earnings";
import CreateEarnings from "pages/Earnings/create-earning";
import Statistics from "pages/Statistics/statistics";
import { API_BASE_URL } from "utils/api";

const axiosInstance = axios.create();
axiosInstance.interceptors.request.use((request: AxiosRequestConfig) => {
  const token = localStorage.getItem("token");
  if (request.headers) {
    request.headers["Authorization"] = `Bearer ${token}`;
  } else {
    request.headers = {
      Authorization: `Bearer ${token}`,
    };
  }

  return request;
});

const clearCachedDashboardStats = () => {
  [
    "currentWeeklyIncomeSum",
    "currentWeeklyHours",
    "currentAverageHourlyRate",
    "currentWeeklyStudents",
    "totalRevenue",
    "averageWeeklyIncome",
    "averageWeeklyHours",
  ].forEach((key) => localStorage.removeItem(key));
};

function App() {
  const baseURL = API_BASE_URL;

  const authProvider: AuthProvider = {
    login: async ({ credential }: CredentialResponse) => {
      if (!credential) {
        return Promise.reject("Invalid credential.");
      }

      const response = await fetch(`${baseURL}/auth/google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ credential }),
      });

      if (!response.ok) {
        return Promise.reject("Google sign-in failed.");
      }

      const data = await response.json();
      clearCachedDashboardStats();
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      return Promise.resolve();
    },
    logout: () => {
      const token = localStorage.getItem("token");

      if (token && typeof window !== "undefined") {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        clearCachedDashboardStats();
        axios.defaults.headers.common = {};
      }

      return Promise.resolve();
    },
    checkError: () => Promise.resolve(),
    checkAuth: async () => {
      const token = localStorage.getItem("token");

      if (token) {
        return Promise.resolve();
      }
      return Promise.reject();
    },

    getPermissions: () => Promise.resolve(),
    getUserIdentity: async () => {
      const user = localStorage.getItem("user");
      if (user) {
        return Promise.resolve(JSON.parse(user));
      }
    },
  };

  return (
    <ColorModeContextProvider>
      <CssBaseline />
      <GlobalStyles styles={{ html: { WebkitFontSmoothing: "auto" } }} />
      <RefineSnackbarProvider>
        <Refine
          dataProvider={dataProvider(
            `${baseURL}`,
            axiosInstance
          )}
          notificationProvider={notificationProvider}
          ReadyPage={ReadyPage}
          catchAll={<ErrorComponent />}
          resources={[
            {
              name: "students",
              list: AllStudents,
              show: StudentDetails,
              create: CreateStudent,
              edit: EditStudent,
              icon: <PeopleAltOutlined />,
            },
            {
              name: "earnings",
              list: AllEarnings,
              create: CreateEarnings,
              icon: <WalletOutlined />,
            },
            {
              name: "statistics",
              list: Statistics,
              icon: <TimelineIcon />,
            },
            {
              name: "lessons",
              list: LessonSchedule,
              icon: <CalendarMonthIcon />,
            },
          ]}
          Title={Title}
          Sider={Sider}
          Layout={Layout}
          Header={Header}
          routerProvider={routerProvider}
          authProvider={authProvider}
          LoginPage={Login}
          DashboardPage={Home}
        />
      </RefineSnackbarProvider>
    </ColorModeContextProvider>
  );
}

export default App;
